import type { DashboardStat, ActivityLog } from '../../types/common';

export interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: 'generate' | 'view' | 'export' | 'conflict' | 'finalize' | 'settings';
  route: string;
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  disabled?: boolean;
}

export interface TimetableProgress {
  phase: string;
  progress: number;
  status: 'completed' | 'in-progress' | 'pending' | 'blocked';
  description: string;
}

export interface SystemStatus {
  component: string;
  status: 'healthy' | 'warning' | 'error';
  message: string;
  lastUpdated: string;
}

// Dashboard statistics
export const mockDashboardStats: DashboardStat[] = [
  {
    label: 'Total Lessons',
    value: 248,
    trend: 'up',
    trendValue: 5,
    color: 'info',
  },
  {
    label: 'Active Conflicts',
    value: 8,
    trend: 'down',
    trendValue: 12,
    color: 'warning',
  },
  {
    label: 'Teachers Assigned',
    value: '45/52',
    trend: 'up',
    trendValue: 3,
    color: 'success',
  },
  {
    label: 'Curriculum Coverage',
    value: '98.5%',
    trend: 'up',
    trendValue: 2.5,
    color: 'success',
  },
];

// Extended stats for detail view
export const mockExtendedStats = {
  timetable: {
    totalLessons: 248,
    generatedAt: '2025-01-16T14:22:00Z',
    version: 3,
    status: 'generated' as const,
  },
  conflicts: {
    total: 8,
    hard: 0,
    soft: 8,
    resolved: 15,
    pending: 8,
  },
  resources: {
    teachersAssigned: 45,
    teachersTotal: 52,
    roomsUsed: 38,
    roomsTotal: 45,
    utilizationRate: 87,
  },
  curriculum: {
    coverage: 98.5,
    yearGroupsCovered: 7,
    yearGroupsTotal: 7,
    subjectsCovered: 24,
    subjectsTotal: 25,
  },
  finalization: {
    yearGroupsFinalized: 2,
    yearGroupsTotal: 7,
    progress: 29,
  },
};

// Recent activity logs
export const mockRecentActivity: ActivityLog[] = [
  {
    id: 'activity-001',
    timestamp: '2025-01-16T14:30:00Z',
    action: 'Conflict resolved',
    entityType: 'conflict',
    entityId: 'conflict-001',
    user: 'John Smith',
    details: 'Resolved teacher double-booking for Ms. Johnson on Monday P1',
  },
  {
    id: 'activity-002',
    timestamp: '2025-01-16T14:22:00Z',
    action: 'Timetable generated',
    entityType: 'timetable',
    entityId: 'timetable-current',
    user: 'System',
    details: 'Generation completed in 2m 34s with 98.5% curriculum coverage',
  },
  {
    id: 'activity-003',
    timestamp: '2025-01-16T13:45:00Z',
    action: 'Teacher added',
    entityType: 'teacher',
    entityId: 'teacher-52',
    user: 'Sarah Wilson',
    details: 'Added Dr. Emily Brown to Science department',
  },
  {
    id: 'activity-004',
    timestamp: '2025-01-16T12:30:00Z',
    action: 'Rule modified',
    entityType: 'rule',
    entityId: 'rule-003',
    user: 'John Smith',
    details: 'Updated maximum consecutive periods rule from 4 to 5',
  },
  {
    id: 'activity-005',
    timestamp: '2025-01-16T11:15:00Z',
    action: 'Year group finalized',
    entityType: 'timetable',
    entityId: 'year-10',
    user: 'John Smith',
    details: 'Year 10 timetable finalized and published to portal',
  },
  {
    id: 'activity-006',
    timestamp: '2025-01-16T10:00:00Z',
    action: 'Simulation created',
    entityType: 'simulation',
    entityId: 'sim-001',
    user: 'Sarah Wilson',
    details: 'Created what-if scenario: "Add new Year 12 options"',
  },
  {
    id: 'activity-007',
    timestamp: '2025-01-15T16:45:00Z',
    action: 'Export completed',
    entityType: 'export',
    entityId: 'export-001',
    user: 'John Smith',
    details: 'Exported full timetable as PDF (2.4 MB)',
  },
  {
    id: 'activity-008',
    timestamp: '2025-01-15T15:20:00Z',
    action: 'Room allocation updated',
    entityType: 'room',
    entityId: 'room-sci-lab-1',
    user: 'System',
    details: 'Science Lab 1 reassigned to resolve capacity conflict',
  },
];

// Quick actions for dashboard
export const mockQuickActions: QuickAction[] = [
  {
    id: 'action-generate',
    title: 'Generate Timetable',
    description: 'Run the automated timetable generator',
    icon: 'generate',
    route: '/sat-v3-zone/generator/generate',
    color: 'primary',
  },
  {
    id: 'action-view',
    title: 'View Timetable',
    description: 'View current timetable with filters',
    icon: 'view',
    route: '/sat-v3-zone/timetable/view',
    color: 'info',
  },
  {
    id: 'action-conflicts',
    title: 'Resolve Conflicts',
    description: '8 conflicts need attention',
    icon: 'conflict',
    route: '/sat-v3-zone/timetable/conflicts',
    color: 'warning',
  },
  {
    id: 'action-export',
    title: 'Export Timetables',
    description: 'Export as PDF, Excel, or CSV',
    icon: 'export',
    route: '/sat-v3-zone/export/timetables',
    color: 'secondary',
  },
  {
    id: 'action-finalize',
    title: 'Finalize Year Groups',
    description: '5 year groups ready to finalize',
    icon: 'finalize',
    route: '/sat-v3-zone/versions/finalize',
    color: 'success',
  },
  {
    id: 'action-settings',
    title: 'Automation Settings',
    description: 'Configure generation parameters',
    icon: 'settings',
    route: '/sat-v3-zone/generator/settings',
    color: 'info',
  },
];

// Timetable progress phases
export const mockTimetableProgress: TimetableProgress[] = [
  {
    phase: 'School Setup',
    progress: 100,
    status: 'completed',
    description: 'Structure, days, calendar configured',
  },
  {
    phase: 'People & Curriculum',
    progress: 100,
    status: 'completed',
    description: 'Teachers, students, subjects defined',
  },
  {
    phase: 'Rules & Constraints',
    progress: 100,
    status: 'completed',
    description: 'Hard and soft rules configured',
  },
  {
    phase: 'Generation',
    progress: 100,
    status: 'completed',
    description: 'Timetable generated successfully',
  },
  {
    phase: 'Conflict Resolution',
    progress: 65,
    status: 'in-progress',
    description: '8 conflicts remaining',
  },
  {
    phase: 'Finalization',
    progress: 29,
    status: 'pending',
    description: '2 of 7 year groups finalized',
  },
];

// System status indicators
export const mockSystemStatus: SystemStatus[] = [
  {
    component: 'Timetable Engine',
    status: 'healthy',
    message: 'Running normally',
    lastUpdated: '2025-01-16T14:30:00Z',
  },
  {
    component: 'Data Synchronization',
    status: 'healthy',
    message: 'All data up to date',
    lastUpdated: '2025-01-16T14:30:00Z',
  },
  {
    component: 'Conflict Detection',
    status: 'warning',
    message: '8 unresolved conflicts',
    lastUpdated: '2025-01-16T14:22:00Z',
  },
  {
    component: 'Portal Publishing',
    status: 'healthy',
    message: 'v3 published',
    lastUpdated: '2025-01-16T10:00:00Z',
  },
];

// Helper functions
export const getActivityIcon = (entityType: string): string => {
  const icons: Record<string, string> = {
    conflict: 'warning',
    timetable: 'calendar',
    teacher: 'person',
    rule: 'rule',
    simulation: 'science',
    export: 'download',
    room: 'room',
  };
  return icons[entityType] || 'info';
};

export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays} days ago`;
};

export const getDashboardSummary = () => ({
  lastGeneration: '2025-01-16T14:22:00Z',
  currentVersion: 3,
  totalConflicts: 8,
  resolvedConflicts: 15,
  yearGroupsFinalized: 2,
  yearGroupsTotal: 7,
  overallProgress: 78,
});

